import React, { useEffect, useState } from 'react';
import { PhoneIcon, UserIcon, TagIcon, TrendingUpIcon, AlertTriangleIcon, SearchIcon, CheckCircleIcon, ChevronRightIcon, ChevronLeftIcon, SaveIcon, PackageIcon, PlusIcon, XIcon, MapPinIcon, ClockIcon, DollarSignIcon, TrashIcon, HistoryIcon, CalendarIcon, EyeIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Badge } from '../components/ui/Badge';
import { Modal, ModalBody } from '../components/ui/Modal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Client, Product } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type TabType = 'speech' | 'historial';
type FlowType = 'ofertas' | 'alta_rotacion' | 'vencimientos' | 'validar_riesgo' | '';
type CallResult = 'venta_realizada' | 'promesa_compra' | 'no_interesado' | 'seguimiento' | 'no_contesto' | 'ocupado' | '';
interface SelectedProduct {
  productoId: string;
  nombre: string;
  sku: string;
  precio: number;
}
interface VencidoCliente {
  productoId: string;
  nombre: string;
  sku: string;
  cantidad: number;
  notas: string;
}
export function CallCenterPage() {
  const {
    user
  } = useAuth();
  const {
    clients
  } = useApp();
  const [activeTab, setActiveTab] = useState<TabType>('speech');
  const [callsHistory, setCallsHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedCall, setSelectedCall] = useState<any | null>(null);
  // Wizard State
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Call Data State
  const [selectedClientId, setSelectedClientId] = useState('');
  const [flow, setFlow] = useState<FlowType>('');
  const [vencidosCliente, setVencidosCliente] = useState<VencidoCliente[]>([]);
  const [offeredProducts, setOfferedProducts] = useState<SelectedProduct[]>([]);
  const [interestedProducts, setInterestedProducts] = useState<SelectedProduct[]>([]);
  const [callResult, setCallResult] = useState<CallResult>('');
  const [notes, setNotes] = useState('');
  // Inventory State
  const [inventory, setInventory] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  // Vencidos Form State
  const [vencidoSearch, setVencidoSearch] = useState('');
  const [vencidoQty, setVencidoQty] = useState(1);
  const [vencidoNotes, setVencidoNotes] = useState('');
  const selectedClient = clients.find(c => c.id === selectedClientId);
  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('call_center_calls').select('*').order('fecha', {
        ascending: false
      }).limit(100);
      if (error) throw error;
      if (data) setCallsHistory(data);
    } catch (err) {
      console.error('Error fetching call history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'historial') {
      fetchHistory();
    }
  }, [activeTab]);
  useEffect(() => {
    const loadInventory = async () => {
      setIsLoadingInventory(true);
      try {
        const allProducts: Product[] = [];
        const PAGE_SIZE = 1000;
        let from = 0;
        let hasMore = true;
        while (hasMore) {
          const {
            data,
            error
          } = await supabaseAdmin.from('products').select('*').order('nombre').range(from, from + PAGE_SIZE - 1);
          if (error) break;
          if (data && data.length > 0) {
            allProducts.push(...data.map((p: any) => ({
              id: p.id,
              nombre: p.nombre,
              sku: p.sku,
              stock: p.stock,
              stockMinimo: p.stock_minimo ?? 0,
              precio: p.precio,
              subTotal: p.sub_total || 0,
              itbis: p.itbis || 0,
              proveedor: p.proveedor,
              fechaVencimiento: p.fecha_vencimiento || null,
              estado: p.estado
            })));
            from += PAGE_SIZE;
            hasMore = data.length === PAGE_SIZE;
          } else {
            hasMore = false;
          }
        }
        setInventory(allProducts);
      } catch (err) {
        console.error('Error loading inventory:', err);
      } finally {
        setIsLoadingInventory(false);
      }
    };
    loadInventory();
  }, []);
  const handleNext = () => {
    if (currentStep === 2 && flow !== 'validar_riesgo') {
      setCurrentStep(4); // Skip step 3 if not validating risk
    } else if (currentStep < 7) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep === 4 && flow !== 'validar_riesgo') {
      setCurrentStep(2); // Go back to 2 if we skipped 3
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const resetForm = () => {
    setCurrentStep(1);
    setSelectedClientId('');
    setFlow('');
    setVencidosCliente([]);
    setOfferedProducts([]);
    setInterestedProducts([]);
    setCallResult('');
    setNotes('');
    setSubmitSuccess(false);
  };
  const handleSaveCall = async () => {
    if (!selectedClient || !user || !flow || !callResult) return;
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabaseAdmin.from('call_center_calls').insert({
        agente_id: user.id,
        agente_nombre: user.nombre,
        cliente_id: selectedClient.id,
        cliente_nombre: selectedClient.nombre,
        cliente_telefono: selectedClient.telefono,
        cliente_zona: selectedClient.zona,
        flujo_seleccionado: flow,
        productos_ofrecidos: offeredProducts,
        productos_interes: interestedProducts,
        productos_vencidos_cliente: vencidosCliente,
        tiene_productos_vencidos: vencidosCliente.length > 0,
        resultado: callResult,
        notas: notes
      });
      if (error) throw error;
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error saving call:', err);
      alert('Error al guardar el registro de la llamada.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleOfferedProduct = (product: Product) => {
    setOfferedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.id);
      if (exists) return prev.filter(p => p.productoId !== product.id);
      return [...prev, {
        productoId: product.id,
        nombre: product.nombre,
        sku: product.sku,
        precio: product.precio
      }];
    });
  };
  const toggleInterestedProduct = (product: SelectedProduct) => {
    setInterestedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.productoId);
      if (exists) return prev.filter(p => p.productoId !== product.productoId);
      return [...prev, product];
    });
  };
  const addVencidoCliente = (product: Product) => {
    setVencidosCliente(prev => [...prev, {
      productoId: product.id,
      nombre: product.nombre,
      sku: product.sku,
      cantidad: vencidoQty,
      notas: vencidoNotes
    }]);
    setVencidoSearch('');
    setVencidoQty(1);
    setVencidoNotes('');
  };
  const removeVencidoCliente = (index: number) => {
    setVencidosCliente(prev => prev.filter((_, i) => i !== index));
  };
  // Filter inventory for search
  const filteredInventory = inventory.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10);
  const filteredVencidoSearch = inventory.filter(p => p.nombre.toLowerCase().includes(vencidoSearch.toLowerCase()) || p.sku.toLowerCase().includes(vencidoSearch.toLowerCase())).slice(0, 5);
  if (submitSuccess) {
    return <div className="space-y-6" data-id="element-508">
        <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-510" />} data-id="element-509" />
        <Card className="text-center py-16 animate-fade-in" data-id="element-511">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-512">
            <CheckCircleIcon className="w-10 h-10" data-id="element-513" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-514">
            ¡Llamada Registrada!
          </h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-515">
            El registro de la llamada con {selectedClient?.nombre} se ha
            guardado exitosamente en el sistema.
          </p>
          <Button onClick={resetForm} size="lg" data-id="element-516">
            Iniciar Nueva Llamada
          </Button>
        </Card>
      </div>;
  }
  return <div className="space-y-6 pb-20" data-id="element-517">
      <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-519" />} data-id="element-518" />

      <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl w-fit mb-6" data-id="element-520">
        <button onClick={() => setActiveTab('speech')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'speech' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-521">
          <PhoneIcon className="w-4 h-4 mr-2" data-id="element-522" />
          Speech Interactivo
        </button>
        <button onClick={() => setActiveTab('historial')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'historial' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-523">
          <HistoryIcon className="w-4 h-4 mr-2" data-id="element-524" />
          Historial de Llamadas
        </button>
      </div>

      {activeTab === 'historial' ? <Card data-id="element-525">
          <CardHeader title="Registro de Llamadas" data-id="element-526" />
          <div className="overflow-x-auto" data-id="element-527">
            <table className="w-full text-sm text-left" data-id="element-528">
              <thead className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200" data-id="element-529">
                <tr data-id="element-530">
                  <th className="px-4 py-3 font-medium" data-id="element-531">Fecha</th>
                  <th className="px-4 py-3 font-medium" data-id="element-532">Agente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-533">Cliente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-534">Flujo</th>
                  <th className="px-4 py-3 font-medium" data-id="element-535">Resultado</th>
                  <th className="px-4 py-3 font-medium" data-id="element-536">Detalles</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-537">Ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-538">
                {isLoadingHistory ? <tr data-id="element-539">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-540">
                      <div className="flex justify-center mb-2" data-id="element-541">
                        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-542" />
                      </div>
                      Cargando historial...
                    </td>
                  </tr> : callsHistory.length === 0 ? <tr data-id="element-543">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-544">
                      No hay registros de llamadas aún.
                    </td>
                  </tr> : callsHistory.map(call => <tr key={call.id} className="hover:bg-slate-50" data-id="element-545">
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-546">
                        <div className="flex items-center text-slate-900" data-id="element-547">
                          <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" data-id="element-548" />
                          {format(new Date(call.fecha), 'd MMM yyyy, h:mm a', {
                    locale: es
                  })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium" data-id="element-549">
                        {call.agente_nombre}
                      </td>
                      <td className="px-4 py-3" data-id="element-550">
                        <p className="text-slate-900 font-medium" data-id="element-551">
                          {call.cliente_nombre}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-552">
                          {call.cliente_zona}
                        </p>
                      </td>
                      <td className="px-4 py-3" data-id="element-553">
                        <Badge variant="secondary" className="capitalize" data-id="element-554">
                          {call.flujo_seleccionado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-555">
                        <Badge variant={call.resultado === 'venta_realizada' ? 'success' : call.resultado === 'promesa_compra' ? 'info' : call.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-556">
                          {call.resultado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-557">
                        <div className="text-xs space-y-1" data-id="element-558">
                          {call.productos_interes?.length > 0 && <p className="text-emerald-600 font-medium" data-id="element-559">
                              {call.productos_interes.length} prod. interesaron
                            </p>}
                          {call.tiene_productos_vencidos && <p className="text-rose-600 font-medium flex items-center" data-id="element-560">
                              <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-561" />
                              Reportó vencidos
                            </p>}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-562">
                        <button onClick={() => setSelectedCall(call)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Ver detalle" data-id="element-563">
                          <EyeIcon className="w-4 h-4" data-id="element-564" />
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card> : <>
          {/* Progress Bar */}
          <div className="mb-8" data-id="element-565">
            <div className="flex items-center justify-between relative" data-id="element-566">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 rounded-full -z-10" data-id="element-567"></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-indigo-600 rounded-full -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / 6 * 100}%`
          }} data-id="element-568"></div>

              {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${step < currentStep ? 'bg-indigo-600 border-indigo-600 text-white' : step === currentStep ? 'bg-white border-indigo-600 text-indigo-600' : 'bg-white border-slate-300 text-slate-400'}`} data-id="element-569">
                  {step < currentStep ? <CheckCircleIcon className="w-4 h-4" data-id="element-570" /> : step}
                </div>)}
            </div>
            <div className="flex justify-between mt-2 text-xs font-medium text-slate-500 px-1" data-id="element-571">
              <span data-id="element-572">Apertura</span>
              <span data-id="element-573">Cierre</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-574">
            {/* Left Column: Script & Guidance */}
            <div className="lg:col-span-1 space-y-6" data-id="element-575">
              <Card className="bg-indigo-50 border-indigo-100 sticky top-6" data-id="element-576">
                <CardHeader title="Guía de Llamada" data-id="element-577" />
                <div className="mt-4 space-y-4" data-id="element-578">
                  {currentStep === 1 && <div className="animate-fade-in" data-id="element-579">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-580">
                        Lee el siguiente texto:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-581">
                        "Hola, ¿hablo con{' '}
                        {selectedClient?.contacto || 'el encargado'}?<br data-id="element-582" />
                        <br data-id="element-583" />
                        Le habla <strong data-id="element-584">{user?.nombre}</strong> de Miladys
                        Rodríguez y Asociados.
                        <br data-id="element-585" />
                        <br data-id="element-586" />
                        Le llamo rápidamente porque estamos ayudando a farmacias
                        como la suya a optimizar sus compras y mejorar la
                        rotación de sus productos.
                        <br data-id="element-587" />
                        <br data-id="element-588" />
                        <strong data-id="element-589">¿Tiene un minuto para comentarle?</strong>"
                      </div>
                    </div>}

                  {currentStep === 2 && <div className="animate-fade-in" data-id="element-590">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-591">
                        Si responde que SÍ:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-592">
                        "Perfecto, gracias. Para ayudarle mejor, le hago una
                        consulta rápida:
                        <br data-id="element-593" />
                        <br data-id="element-594" />
                        <strong data-id="element-595">¿Le interesa más ahora mismo:</strong>
                        <br data-id="element-596" />
                        • ver productos en oferta,
                        <br data-id="element-597" />
                        • productos que se están vendiendo rápido,
                        <br data-id="element-598" />
                        • revisar opciones para evitar pérdidas por vencimiento,
                        <br data-id="element-599" />• o validar productos que usted ya tenga en
                        riesgo?"
                      </div>
                    </div>}

                  {currentStep === 3 && <div className="animate-fade-in" data-id="element-600">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-601">
                        Registro de Vencimientos:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-602">
                        "Entiendo.{' '}
                        <strong data-id="element-603">
                          ¿Cuáles productos tiene en esa condición?
                        </strong>
                        "
                        <br data-id="element-604" />
                        <br data-id="element-605" />
                        <span className="text-xs text-rose-600 font-bold not-italic block mt-2" data-id="element-606">
                          🔴 IMPORTANTE: Solo registrar, NO ofrecer devolución
                          en esta llamada.
                        </span>
                        <br data-id="element-607" />
                        "Perfecto, tomo nota de esos productos. Eso le puede
                        estar generando pérdidas si no se mueven a tiempo. En
                        base a eso, le puedo ayudar con opciones que se vendan
                        más rápido o complementen esos productos."
                      </div>
                    </div>}

                  {currentStep === 4 && <div className="animate-fade-in" data-id="element-608">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-609">
                        Presentar Opciones:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-610">
                        "Le comento, tengo algunas opciones que le pueden
                        funcionar:"
                        <br data-id="element-611" />
                        <br data-id="element-612" />
                        <ul className="list-disc pl-4 space-y-1 mt-2" data-id="element-613">
                          {offeredProducts.length === 0 ? <li className="text-slate-400" data-id="element-614">
                              Selecciona productos a la derecha...
                            </li> : offeredProducts.map(p => <li key={p.productoId} data-id="element-615">
                                <strong data-id="element-616">{p.nombre}</strong>
                              </li>)}
                        </ul>
                      </div>
                    </div>}

                  {currentStep === 5 && <div className="animate-fade-in" data-id="element-617">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-618">
                        Medir Interés:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-619">
                        "<strong data-id="element-620">¿Cuál de estos le interesa más?</strong>
                        <br data-id="element-621" />o ¿Prefiere que le recomiende el que más salida
                        está teniendo en su zona?"
                      </div>
                    </div>}

                  {currentStep === 6 && <div className="animate-fade-in" data-id="element-622">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-623">
                        Ajuste Dinámico (Si duda):
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-624">
                        "Puedo ajustarle opciones:
                        <br data-id="element-625" />
                        • más económicas
                        <br data-id="element-626" />
                        • con mayor ganancia
                        <br data-id="element-627" />
                        • de más rotación"
                        <br data-id="element-628" />
                        <br data-id="element-629" />
                        <span className="text-xs text-indigo-600 font-bold not-italic block" data-id="element-630">
                          Vuelve al paso 4 si necesitas buscar otros productos.
                        </span>
                      </div>
                    </div>}

                  {currentStep === 7 && <div className="animate-fade-in" data-id="element-631">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-632">
                        Cierre:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-633">
                        "
                        <strong data-id="element-634">
                          ¿Le gustaría probar con este producto en su próximo
                          pedido?
                        </strong>
                        "<br data-id="element-635" />
                        <br data-id="element-636" />
                        <span className="text-xs text-slate-500 not-italic block" data-id="element-637">
                          Registra el resultado final de la llamada y guarda.
                        </span>
                      </div>
                    </div>}
                </div>
              </Card>
            </div>

            {/* Right Column: Interactive Workspace */}
            <div className="lg:col-span-2" data-id="element-638">
              <Card className="min-h-[500px] flex flex-col" data-id="element-639">
                <div className="flex-1" data-id="element-640">
                  {/* STEP 1: APERTURA */}
                  {currentStep === 1 && <div className="space-y-6 animate-fade-in" data-id="element-641">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-642">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-643">
                          <UserIcon className="w-5 h-5" data-id="element-644" />
                        </div>
                        <div data-id="element-645">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-646">
                            1. Apertura de Llamada
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-647">
                            Selecciona el cliente a contactar
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-648">
                        <SearchableSelect label="Buscar Cliente / Farmacia" options={clients.map(c => ({
                    value: c.id,
                    label: `${c.nombre} - ${c.zona}`
                  }))} value={selectedClientId} onChange={setSelectedClientId} placeholder="Escribe para buscar..." data-id="element-649" />

                        {selectedClient && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3" data-id="element-650">
                            <div className="flex items-start justify-between" data-id="element-651">
                              <div data-id="element-652">
                                <h4 className="font-bold text-slate-900 text-lg" data-id="element-653">
                                  {selectedClient.nombre}
                                </h4>
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1" data-id="element-654">
                                  <MapPinIcon className="w-4 h-4" data-id="element-655" />{' '}
                                  {selectedClient.zona} |{' '}
                                  {selectedClient.ciudad}
                                </p>
                              </div>
                              <Badge variant="info" data-id="element-656">
                                {selectedClient.codigo}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200" data-id="element-657">
                              <div data-id="element-658">
                                <p className="text-xs text-slate-500" data-id="element-659">
                                  Teléfono
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-660">
                                  {selectedClient.telefono || 'No registrado'}
                                </p>
                              </div>
                              <div data-id="element-661">
                                <p className="text-xs text-slate-500" data-id="element-662">
                                  Contacto
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-663">
                                  {selectedClient.contacto || 'No registrado'}
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>

                      {selectedClient && <div className="pt-6 grid grid-cols-3 gap-3" data-id="element-664">
                          <Button variant="danger" className="w-full" onClick={() => {
                    setCallResult('no_contesto');
                    setCurrentStep(7);
                  }} data-id="element-665">
                            No Contestó
                          </Button>
                          <Button variant="warning" className="w-full" onClick={() => {
                    setCallResult('ocupado');
                    setCurrentStep(7);
                  }} data-id="element-666">
                            Ocupado
                          </Button>
                          <Button variant="primary" className="w-full" onClick={handleNext} data-id="element-667">
                            Cliente Disponible
                          </Button>
                        </div>}
                    </div>}

                  {/* STEP 2: DETECCIÓN */}
                  {currentStep === 2 && <div className="space-y-6 animate-fade-in" data-id="element-668">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-669">
                        <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center" data-id="element-670">
                          <SearchIcon className="w-5 h-5" data-id="element-671" />
                        </div>
                        <div data-id="element-672">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-673">
                            2. Detección de Interés
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-674">
                            ¿Qué le interesa más al cliente?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-675">
                        <button onClick={() => setFlow('ofertas')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'ofertas' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`} data-id="element-676">
                          <TagIcon className={`w-8 h-8 mb-3 ${flow === 'ofertas' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-677" />
                          <h4 className="font-bold text-slate-900" data-id="element-678">
                            Productos en Oferta
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-679">
                            Promociones y descuentos actuales
                          </p>
                        </button>

                        <button onClick={() => setFlow('alta_rotacion')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'alta_rotacion' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'}`} data-id="element-680">
                          <TrendingUpIcon className={`w-8 h-8 mb-3 ${flow === 'alta_rotacion' ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-681" />
                          <h4 className="font-bold text-slate-900" data-id="element-682">
                            Alta Rotación
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-683">
                            Lo que más se está vendiendo
                          </p>
                        </button>

                        <button onClick={() => setFlow('vencimientos')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'vencimientos' ? 'border-amber-600 bg-amber-50' : 'border-slate-200 hover:border-amber-300 hover:bg-slate-50'}`} data-id="element-684">
                          <ClockIcon className={`w-8 h-8 mb-3 ${flow === 'vencimientos' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-685" />
                          <h4 className="font-bold text-slate-900" data-id="element-686">
                            Evitar Vencimientos
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-687">
                            Opciones con fecha larga
                          </p>
                        </button>

                        <button onClick={() => setFlow('validar_riesgo')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'validar_riesgo' ? 'border-rose-600 bg-rose-50' : 'border-slate-200 hover:border-rose-300 hover:bg-slate-50'}`} data-id="element-688">
                          <AlertTriangleIcon className={`w-8 h-8 mb-3 ${flow === 'validar_riesgo' ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-689" />
                          <h4 className="font-bold text-slate-900" data-id="element-690">
                            Validar Riesgo
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-691">
                            Productos del cliente por vencer
                          </p>
                        </button>
                      </div>
                    </div>}

                  {/* STEP 3: VENCIDOS DEL CLIENTE (Only if validar_riesgo) */}
                  {currentStep === 3 && <div className="space-y-6 animate-fade-in" data-id="element-692">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-693">
                        <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center" data-id="element-694">
                          <AlertTriangleIcon className="w-5 h-5" data-id="element-695" />
                        </div>
                        <div data-id="element-696">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-697">
                            3. Registro de Vencimientos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-698">
                            Registra los productos que el cliente menciona
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4" data-id="element-699">
                        <div className="relative" data-id="element-700">
                          <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-701" />
                          <input type="text" placeholder="Buscar producto en inventario..." value={vencidoSearch} onChange={e => setVencidoSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-702" />
                        </div>

                        {vencidoSearch && filteredVencidoSearch.length > 0 && <div className="bg-white border border-slate-200 rounded-lg shadow-sm max-h-48 overflow-y-auto" data-id="element-703">
                            {filteredVencidoSearch.map(p => <div key={p.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-704">
                                <div data-id="element-705">
                                  <p className="font-medium text-sm text-slate-900" data-id="element-706">
                                    {p.nombre}
                                  </p>
                                  <p className="text-xs text-slate-500" data-id="element-707">
                                    SKU: {p.sku}
                                  </p>
                                </div>
                                <Button size="sm" variant="secondary" onClick={() => {
                        setVencidoSearch(p.nombre);
                        // We don't add immediately, let them set qty/notes
                      }} data-id="element-708">
                                  Seleccionar
                                </Button>
                              </div>)}
                          </div>}

                        <div className="grid grid-cols-3 gap-3" data-id="element-709">
                          <div className="col-span-1" data-id="element-710">
                            <Input type="number" label="Cantidad" value={vencidoQty} onChange={e => setVencidoQty(Number(e.target.value))} min={1} data-id="element-711" />
                          </div>
                          <div className="col-span-2" data-id="element-712">
                            <Input label="Notas (Lote, Fecha aprox)" value={vencidoNotes} onChange={e => setVencidoNotes(e.target.value)} placeholder="Ej: Vence en 2 meses" data-id="element-713" />
                          </div>
                        </div>

                        <Button className="w-full" disabled={!vencidoSearch} onClick={() => {
                    const prod = inventory.find(p => p.nombre === vencidoSearch || p.sku === vencidoSearch);
                    if (prod) {
                      addVencidoCliente(prod);
                    } else {
                      // Allow adding custom text if not in inventory
                      setVencidosCliente(prev => [...prev, {
                        productoId: 'custom',
                        nombre: vencidoSearch,
                        sku: 'N/A',
                        cantidad: vencidoQty,
                        notas: vencidoNotes
                      }]);
                      setVencidoSearch('');
                      setVencidoQty(1);
                      setVencidoNotes('');
                    }
                  }} data-id="element-714">
                          <PlusIcon className="w-4 h-4 mr-2" data-id="element-715" /> Agregar a la
                          lista
                        </Button>
                      </div>

                      {vencidosCliente.length > 0 && <div className="space-y-2" data-id="element-716">
                          <h4 className="text-sm font-bold text-slate-700" data-id="element-717">
                            Productos Registrados:
                          </h4>
                          {vencidosCliente.map((v, i) => <div key={i} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200" data-id="element-718">
                              <div data-id="element-719">
                                <p className="font-medium text-sm text-slate-900" data-id="element-720">
                                  {v.nombre}{' '}
                                  <span className="text-slate-500 font-normal" data-id="element-721">
                                    x{v.cantidad}
                                  </span>
                                </p>
                                {v.notas && <p className="text-xs text-slate-500" data-id="element-722">
                                    {v.notas}
                                  </p>}
                              </div>
                              <button onClick={() => removeVencidoCliente(i)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg" data-id="element-723">
                                <TrashIcon className="w-4 h-4" data-id="element-724" />
                              </button>
                            </div>)}
                        </div>}
                    </div>}

                  {/* STEP 4: MOSTRAR PRODUCTOS */}
                  {currentStep === 4 && <div className="space-y-6 animate-fade-in" data-id="element-725">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-726">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-727">
                          <PackageIcon className="w-5 h-5" data-id="element-728" />
                        </div>
                        <div data-id="element-729">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-730">
                            4. Selección de Productos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-731">
                            Elige los productos a ofrecer al cliente
                          </p>
                        </div>
                      </div>

                      <div className="relative" data-id="element-732">
                        <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-733" />
                        <input type="text" placeholder="Buscar en inventario..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-734" />
                      </div>

                      <div className="bg-slate-50 rounded-xl border border-slate-200 h-[300px] overflow-y-auto p-2" data-id="element-735">
                        {isLoadingInventory ? <div className="flex items-center justify-center h-full" data-id="element-736">
                            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-737" />
                          </div> : filteredInventory.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-738">
                            <PackageIcon className="w-8 h-8 mb-2 opacity-50" data-id="element-739" />
                            <p className="text-sm" data-id="element-740">
                              No se encontraron productos
                            </p>
                          </div> : <div className="space-y-2" data-id="element-741">
                            {filteredInventory.map(product => {
                      const isSelected = offeredProducts.some(p => p.productoId === product.id);
                      return <div key={product.id} onClick={() => toggleOfferedProduct(product)} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-300'}`} data-id="element-742">
                                  <div className="flex-1 min-w-0 pr-4" data-id="element-743">
                                    <p className={`font-medium text-sm truncate ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-744">
                                      {product.nombre}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1" data-id="element-745">
                                      <span className="text-xs text-slate-500" data-id="element-746">
                                        SKU: {product.sku}
                                      </span>
                                      <span className="text-xs font-medium text-emerald-600" data-id="element-747">
                                        RD$ {product.precio}
                                      </span>
                                      <span className={`text-xs ${product.stock > product.stockMinimo ? 'text-slate-500' : 'text-rose-500'}`} data-id="element-748">
                                        Stock: {product.stock}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300'}`} data-id="element-749">
                                    {isSelected && <CheckCircleIcon className="w-3 h-3" data-id="element-750" />}
                                  </div>
                                </div>;
                    })}
                          </div>}
                      </div>

                      {offeredProducts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-751">
                          {offeredProducts.map(p => <span key={p.productoId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium" data-id="element-752">
                              {p.nombre}
                              <button onClick={() => setOfferedProducts(prev => prev.filter(x => x.productoId !== p.productoId))} data-id="element-753">
                                <XIcon className="w-3 h-3 hover:text-indigo-900" data-id="element-754" />
                              </button>
                            </span>)}
                        </div>}
                    </div>}

                  {/* STEP 5: INTERÉS DEL CLIENTE */}
                  {currentStep === 5 && <div className="space-y-6 animate-fade-in" data-id="element-755">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-756">
                        <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center" data-id="element-757">
                          <CheckCircleIcon className="w-5 h-5" data-id="element-758" />
                        </div>
                        <div data-id="element-759">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-760">
                            5. Interés del Cliente
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-761">
                            ¿Cuáles productos le interesaron?
                          </p>
                        </div>
                      </div>

                      {offeredProducts.length === 0 ? <div className="text-center py-8 text-slate-500" data-id="element-762">
                          No se ofrecieron productos en el paso anterior.
                        </div> : <div className="space-y-3" data-id="element-763">
                          {offeredProducts.map(product => {
                    const isInterested = interestedProducts.some(p => p.productoId === product.productoId);
                    return <div key={product.productoId} onClick={() => toggleInterestedProduct(product)} className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${isInterested ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`} data-id="element-764">
                                <div data-id="element-765">
                                  <p className={`font-bold ${isInterested ? 'text-amber-900' : 'text-slate-900'}`} data-id="element-766">
                                    {product.nombre}
                                  </p>
                                  <p className="text-sm text-slate-500" data-id="element-767">
                                    RD$ {product.precio}
                                  </p>
                                </div>
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${isInterested ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300'}`} data-id="element-768">
                                  {isInterested && <CheckCircleIcon className="w-4 h-4" data-id="element-769" />}
                                </div>
                              </div>;
                  })}
                        </div>}
                    </div>}

                  {/* STEP 6: AJUSTE */}
                  {currentStep === 6 && <div className="space-y-6 animate-fade-in" data-id="element-770">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-771">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-772">
                          <TrendingUpIcon className="w-5 h-5" data-id="element-773" />
                        </div>
                        <div data-id="element-774">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-775">
                            6. Ajuste de Oferta
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-776">
                            Si el cliente dudó, ¿qué busca?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3" data-id="element-777">
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('economico');
                    setCurrentStep(4);
                  }} data-id="element-778">
                          <DollarSignIcon className="w-4 h-4 mr-3 text-emerald-600" data-id="element-779" />{' '}
                          Buscar opciones más económicas
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('ganancia');
                    setCurrentStep(4);
                  }} data-id="element-780">
                          <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-600" data-id="element-781" />{' '}
                          Buscar opciones con mayor margen
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('rotacion');
                    setCurrentStep(4);
                  }} data-id="element-782">
                          <ClockIcon className="w-4 h-4 mr-3 text-amber-600" data-id="element-783" />{' '}
                          Buscar opciones de alta rotación
                        </Button>
                      </div>

                      <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center" data-id="element-784">
                        <p className="text-sm text-slate-600 mb-3" data-id="element-785">
                          Si el cliente ya está convencido o no desea más
                          opciones:
                        </p>
                        <Button onClick={handleNext} className="w-full" data-id="element-786">
                          Ir al Cierre
                        </Button>
                      </div>
                    </div>}

                  {/* STEP 7: CIERRE Y REGISTRO */}
                  {currentStep === 7 && <div className="space-y-6 animate-fade-in" data-id="element-787">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-788">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-789">
                          <SaveIcon className="w-5 h-5" data-id="element-790" />
                        </div>
                        <div data-id="element-791">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-792">
                            7. Cierre y Registro
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-793">
                            Guarda el resultado de la llamada
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-794">
                        <div data-id="element-795">
                          <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-796">
                            Resultado de la Llamada *
                          </label>
                          <div className="grid grid-cols-2 gap-3" data-id="element-797">
                            <button onClick={() => setCallResult('venta_realizada')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'venta_realizada' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`} data-id="element-798">
                              Venta Realizada
                            </button>
                            <button onClick={() => setCallResult('promesa_compra')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'promesa_compra' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'}`} data-id="element-799">
                              Promesa de Compra
                            </button>
                            <button onClick={() => setCallResult('seguimiento')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'seguimiento' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'}`} data-id="element-800">
                              Requiere Seguimiento
                            </button>
                            <button onClick={() => setCallResult('no_interesado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_interesado' ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300'}`} data-id="element-801">
                              No Interesado
                            </button>
                            <button onClick={() => setCallResult('no_contesto')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_contesto' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-802">
                              No Contestó
                            </button>
                            <button onClick={() => setCallResult('ocupado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'ocupado' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-803">
                              Ocupado
                            </button>
                          </div>
                        </div>

                        <Textarea label="Notas Adicionales" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Comentarios sobre la llamada, objeciones, o detalles para el seguimiento..." rows={4} data-id="element-804" />

                        {/* Summary Box */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm" data-id="element-805">
                          <h4 className="font-bold text-slate-900 mb-2" data-id="element-806">
                            Resumen a Guardar:
                          </h4>
                          <ul className="space-y-1 text-slate-600" data-id="element-807">
                            <li data-id="element-808">
                              • <strong data-id="element-809">Cliente:</strong>{' '}
                              {selectedClient?.nombre}
                            </li>
                            <li data-id="element-810">
                              • <strong data-id="element-811">Flujo:</strong> {flow || 'N/A'}
                            </li>
                            <li data-id="element-812">
                              • <strong data-id="element-813">Ofrecidos:</strong>{' '}
                              {offeredProducts.length} productos
                            </li>
                            <li data-id="element-814">
                              • <strong data-id="element-815">Interesaron:</strong>{' '}
                              {interestedProducts.length} productos
                            </li>
                            <li data-id="element-816">
                              • <strong data-id="element-817">Vencimientos reportados:</strong>{' '}
                              {vencidosCliente.length}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Navigation Footer */}
                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-818">
                  <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} data-id="element-819">
                    <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-820" /> Atrás
                  </Button>

                  {currentStep < 7 ? <Button onClick={handleNext} disabled={currentStep === 1 && !selectedClient || currentStep === 2 && !flow} data-id="element-821">
                      Siguiente <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-822" />
                    </Button> : <Button onClick={handleSaveCall} disabled={!callResult || isSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-823">
                      {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-824" /> : <SaveIcon className="w-4 h-4 mr-2" data-id="element-825" />}
                      Guardar Llamada
                    </Button>}
                </div>
              </Card>
            </div>
          </div>
        </>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedCall} onClose={() => setSelectedCall(null)} title="Detalle de Llamada" size="lg" data-id="element-826">
        {selectedCall && <ModalBody data-id="element-827">
            <div className="space-y-6" data-id="element-828">
              {/* Client & Call Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-829">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-830">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-831">Cliente</p>
                  <p className="font-bold text-slate-900 text-lg" data-id="element-832">
                    {selectedCall.cliente_nombre}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-833">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-834" />
                    {selectedCall.cliente_zona}
                  </div>
                  {selectedCall.cliente_telefono && <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-835">
                      <PhoneIcon className="w-3.5 h-3.5" data-id="element-836" />
                      {selectedCall.cliente_telefono}
                    </div>}
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-837">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-838">
                    Información de Llamada
                  </p>
                  <div className="space-y-2" data-id="element-839">
                    <div className="flex items-center gap-2 text-sm" data-id="element-840">
                      <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-841" />
                      <span className="text-slate-700" data-id="element-842">
                        {selectedCall.agente_nombre}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-843">
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-844" />
                      <span className="text-slate-700" data-id="element-845">
                        {format(new Date(selectedCall.fecha), 'd MMM yyyy, h:mm a', {
                      locale: es
                    })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2" data-id="element-846">
                      <Badge variant="secondary" className="capitalize" data-id="element-847">
                        {selectedCall.flujo_seleccionado.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant={selectedCall.resultado === 'venta_realizada' ? 'success' : selectedCall.resultado === 'promesa_compra' ? 'info' : selectedCall.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-848">
                        {selectedCall.resultado.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Productos Ofrecidos */}
              {selectedCall.productos_ofrecidos?.length > 0 && <div data-id="element-849">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-850">
                    <PackageIcon className="w-4 h-4 text-indigo-500" data-id="element-851" />
                    Productos Ofrecidos (
                    {selectedCall.productos_ofrecidos.length})
                  </h4>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-852">
                    <table className="w-full text-sm" data-id="element-853">
                      <thead className="bg-slate-50 text-xs text-slate-500" data-id="element-854">
                        <tr data-id="element-855">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-856">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-857">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-right font-medium" data-id="element-858">
                            Precio
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-859">
                        {selectedCall.productos_ofrecidos.map((p: any, i: number) => <tr key={i} className="hover:bg-slate-50" data-id="element-860">
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-861">
                                {p.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-862">
                                {p.sku}
                              </td>
                              <td className="px-3 py-2 text-right text-emerald-600 font-medium" data-id="element-863">
                                RD$ {p.precio}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Productos de Interés */}
              {selectedCall.productos_interes?.length > 0 && <div data-id="element-864">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-865">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-866" />
                    Productos de Interés (
                    {selectedCall.productos_interes.length})
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-867">
                    {selectedCall.productos_interes.map((p: any, i: number) => <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200" data-id="element-868">
                        {p.nombre} — RD$ {p.precio}
                      </span>)}
                  </div>
                </div>}

              {/* Productos Vencidos del Cliente */}
              {selectedCall.productos_vencidos_cliente?.length > 0 && <div data-id="element-869">
                  <h4 className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2" data-id="element-870">
                    <AlertTriangleIcon className="w-4 h-4 text-rose-500" data-id="element-871" />
                    Productos Vencidos Reportados (
                    {selectedCall.productos_vencidos_cliente.length})
                  </h4>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl overflow-hidden" data-id="element-872">
                    <table className="w-full text-sm" data-id="element-873">
                      <thead className="bg-rose-100 text-xs text-rose-700" data-id="element-874">
                        <tr data-id="element-875">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-876">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-877">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-center font-medium" data-id="element-878">
                            Cantidad
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-879">
                            Notas
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100" data-id="element-880">
                        {selectedCall.productos_vencidos_cliente.map((v: any, i: number) => <tr key={i} data-id="element-881">
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-882">
                                {v.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-883">
                                {v.sku}
                              </td>
                              <td className="px-3 py-2 text-center font-bold text-rose-700" data-id="element-884">
                                {v.cantidad}
                              </td>
                              <td className="px-3 py-2 text-slate-600 text-xs" data-id="element-885">
                                {v.notas || '—'}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Notas */}
              {selectedCall.notas && <div data-id="element-886">
                  <h4 className="text-sm font-bold text-slate-700 mb-2" data-id="element-887">
                    Notas
                  </h4>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap" data-id="element-888">
                    {selectedCall.notas}
                  </div>
                </div>}
            </div>
          </ModalBody>}
      </Modal>
    </div>;
}